import styled from 'styled-components'
import { FC, FormEvent } from 'react'
import { Box, Switch, switchClasses } from '@mui/material'
import Button from '../../../../shared/Button'
import Hint from '../../../../shared/hint/Hint'
import { SEARCH_HINT, STEMMING_HINT } from '../../constants'
import SearchInput from '../../../../shared/SearchInput'
import { useKeywordsStateContext } from '../../state/useKeywordsState'
import { prepareDatasetsRequest, trackKeywords } from '../../utils'
import ExportButton from '../../../../shared/exportButton/exportButton'
import { useFiltersStateContext } from '../../state/useFiltersState'
import { useFiltersQuery } from '../../queries/useFiltersQuery'
import { TExportPayload } from '../../../../shared/exportButton/definitions'

const TitleContainerStyled = styled.div`
  display: flex;
  align-items: center;
`

const TitleStyled = styled.h1`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.black};
  margin-right: 4px;
`

const SubTitleStyled = styled.span`
  display: block;
  margin: 6px 0 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey.dark};
  line-height: 16px;
`

const SearchFieldContainerStyled = styled.form`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: 10px;
  align-items: center;
  max-width: 656px;
`

const StemmingToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-self: center;
`

const SwitchStyled = styled(Switch)`
  .${switchClasses.track} {
    border-radius: 14px;
    background-color: ${({ theme }) => theme.colors.grey.secondary};
    opacity: 1;
  }

  .${switchClasses.switchBase} {
    padding: 8px;
  }

  .${switchClasses.switchBase}.${switchClasses.checked} {
    background-color: initial!important;
    color: ${({ theme }) => theme.colors.white};
  }

  .${switchClasses.switchBase}.${switchClasses.checked} + .${switchClasses.track} {
    background-color: ${({ theme }) => theme.colors.blue.primary};
    opacity: 1;
  }
`
const SwitchLabelStyled = styled.div`
  font-size: 12px;
  font-weight: 500;
  position: relative;
  top: 7px;
  left: 4px;
`

const useExportPayload = () => {
  const { submittedKeywords, includeStemmingSearch } = useKeywordsStateContext()
  const { filters: selectedFilters } = useFiltersStateContext()
  const filtersData = useFiltersQuery(false).data

  return {
    getExportPayload: (): TExportPayload => ({
      type: 'SEARCH_RESULTS',
      search: prepareDatasetsRequest(selectedFilters, filtersData, submittedKeywords, includeStemmingSearch)
    }),
    isExportDisabled: !filtersData
  }
}

const SearchField: FC<{ className?: string }> = ({ className }) => {
  const {
    keywords,
    setKeywords,
    setSubmittedKeywords,
    includeStemmingSearch,
    setIncludeStemmingSearch
  } = useKeywordsStateContext()
  const { getExportPayload, isExportDisabled } = useExportPayload()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSubmittedKeywords(keywords)
    trackKeywords(keywords)
  }

  const handleClear = () => {
    setKeywords('')
    setSubmittedKeywords('')
  }

  return (
    <div className={className}>
      <Box display="flex" justifyContent="space-between">
        <TitleContainerStyled>
          <TitleStyled>Search for variables</TitleStyled>
          <Hint
            withDefaultColor
            placement="top"
            title={SEARCH_HINT}
            maxWidth={514}
          />
        </TitleContainerStyled>
        <ExportButton getExportPayload={getExportPayload} disabled={isExportDisabled} />
      </Box>
      <SubTitleStyled>
        Enter search terms(s) or phrase(s) in quotation marks
      </SubTitleStyled>
      <SearchFieldContainerStyled onSubmit={handleSubmit}>
        <SearchInput
          fullWidth
          onClear={handleClear}
          value={keywords}
          placeholder="For example “Health status” Smoke Income"
          onChange={(e) => setKeywords(e.target.value)}
        />
        <Box mr={1}>
          <SwitchLabelStyled>Include stemming</SwitchLabelStyled>
          <StemmingToggleWrapper>
            <SwitchStyled
              checked={includeStemmingSearch}
              onChange={(e) => setIncludeStemmingSearch(e.target.checked)}
              inputProps={{ 'aria-label': 'Include stemming search results' }}
            />
            <Hint
              withDefaultColor
              placement="bottom"
              title={STEMMING_HINT}
              maxWidth={520}
            />
          </StemmingToggleWrapper>
        </Box>
        <Button type="submit">
          Search
        </Button>
      </SearchFieldContainerStyled>
    </div>
  )
}

export default SearchField
